import { audioFilePath, coverUrl, getItem } from '../api/abs';
import { refreshAccessToken } from '../api/client';
import { getSession, sessionStore } from '../api/session';
import type { Chapter, LibraryItem } from '../api/types';
import { resolveServerPath } from '../api/url';
import { dbDelete, dbGetAll, dbPut } from '../lib/db';
import { notify } from '../lib/notify';
import { createStore } from '../lib/store';
import { settingsStore } from '../settings';
import { belongsTo, downloadFolderFor, downloadKey, isDownloadKey } from './keys';
import { DOWNLOADS_DIR, type DownloadFileSpec, type FromWorker, type ToWorker } from './protocol';
import { rowMetadata, stripItem, withRowMetadata } from './recordMeta';
import { isUnplayableLocalFile } from './verify';

export { downloadKey } from './keys';

export type DownloadStatus = 'queued' | 'downloading' | 'paused' | 'complete' | 'error';

export interface DownloadedTrack {
  index: number;
  ino: string;
  fileName: string;
  mimeType: string;
  duration: number;
  startOffset: number;
  size: number;
}

export interface DownloadRecord {
  itemId: string;
  serverUrl: string;
  userId: string;
  /** OPFS folder inside `downloads/`; absent on records made before downloads were per account (`<itemId>`). */
  folder?: string;
  title: string;
  author: string;
  narrator?: string;
  duration: number;
  chapters: Chapter[];
  tracks: DownloadedTrack[];
  coverBlob?: Blob;
  status: DownloadStatus;
  error?: string;
  /** The last error was transient (network, 5xx): resume automatically. */
  retryable?: boolean;
  totalBytes: number;
  downloadedBytes: number;
  createdAt: number;
  completedAt?: number;
  /** Trimmed item JSON for offline book pages. */
  item: LibraryItem;
  // Metadata for download rows and offline book cards (absent on records made by older versions until backfilled).
  libraryId?: string;
  subtitle?: string | null;
  seriesName?: string | null;
  genres?: string[];
  publishedYear?: number | null;
  addedAt?: number;
  updatedAt?: number;
  /** Server-reported size of the book's audio, bytes. */
  sizeBytes?: number | null;
  /** Audio codec, e.g. `aac`. */
  codec?: string | null;
  /** Container / file type without the dot, e.g. `m4b`. */
  container?: string | null;
  /** Bits per second. */
  bitrate?: number | null;
  /** Bytes of the stored cover image. */
  coverBytes?: number;
  /**
   * Whether the server has a cover for the book (from the item's `coverPath`,
   * or the cover request's answer when that was unknown). Absent when unknown
   * (records made by older versions whose cover could not be stored).
   */
  serverHasCover?: boolean;
}

/**
 * UI-facing state: everything but the heavy blobs, plus the record's
 * `downloadKey`. `hasCover` = a cover image is stored on this device;
 * `serverHasCover` = what the server says (see `DownloadRecord`).
 */
export type DownloadSummary = Omit<DownloadRecord, 'coverBlob' | 'item'> & { hasCover: boolean; key: string };

/**
 * The download shown for each item id: the signed-in account's record, else
 * another account's (oldest first). Use `downloadedIds` (data/models.ts) for
 * what the signed-in account can play offline, `listDownloads()` for every
 * account's records.
 */
export const downloadsStore = createStore<Record<string, DownloadSummary>>({});

/** Every account's records, by `downloadKey`. */
const records = new Map<string, DownloadRecord>();
const summaries = new Map<string, DownloadSummary>();
let worker: Worker | null = null;
/** The running download: its record, each file's offset in the book's bytes, and the bytes each finished file has. */
let activeJob: { jobId: string; key: string; fileBase: number[]; fileBytes: number[] } | null = null;
let initialised: Promise<void> | null = null;

export function isDownloadSupported(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.storage?.getDirectory && typeof Worker !== 'undefined';
}

const keyOf = (r: Pick<DownloadRecord, 'serverUrl' | 'userId' | 'itemId'>): string => downloadKey(r, r.itemId);
const folderOf = (r: Pick<DownloadRecord, 'folder' | 'itemId'>): string => r.folder ?? r.itemId;

/** The server's cover answer from an item: its `coverPath` (null = no cover), undefined when the item does not say. */
export function serverCoverFromItem(item: LibraryItem | undefined): boolean | undefined {
  const media = item?.media as { coverPath?: string | null } | undefined;
  if (!media || !('coverPath' in media) || media.coverPath === undefined) return undefined;
  return media.coverPath !== null && media.coverPath !== '';
}

function summarize(r: DownloadRecord): DownloadSummary {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { coverBlob, item, ...rest } = r;
  const serverHasCover = r.serverHasCover ?? serverCoverFromItem(item);
  return { ...rest, hasCover: !!coverBlob, key: keyOf(r), ...(serverHasCover !== undefined ? { serverHasCover } : {}) };
}

function preferred(a: DownloadSummary, b: DownloadSummary): DownloadSummary {
  const session = getSession();
  const aOwn = belongsTo(a, session);
  if (aOwn !== belongsTo(b, session)) return aOwn ? a : b;
  return a.createdAt <= b.createdAt ? a : b;
}

function refreshStore(): void {
  const visible: Record<string, DownloadSummary> = {};
  for (const s of summaries.values()) {
    const current = visible[s.itemId];
    visible[s.itemId] = current ? preferred(current, s) : s;
  }
  downloadsStore.set(visible);
}

function publish(r: DownloadRecord) {
  const key = keyOf(r);
  records.set(key, r);
  summaries.set(key, summarize(r));
  refreshStore();
}

async function persist(r: DownloadRecord) {
  publish(r);
  try {
    await dbPut('downloads', r);
  } catch (err) {
    console.warn('[downloads] could not persist record', err);
  }
}

/** The record behind a `downloadKey`, or the visible download of an item id. */
function recordFor(itemIdOrKey: string): DownloadRecord | undefined {
  if (isDownloadKey(itemIdOrKey)) return records.get(itemIdOrKey);
  const visible = downloadsStore.get()[itemIdOrKey];
  return visible ? records.get(visible.key) : undefined;
}

/** The signed-in account's complete download of an item (any account's when signed out). */
function playableRecord(itemId: string): DownloadRecord | undefined {
  const session = getSession();
  if (session) {
    const own = records.get(downloadKey(session, itemId));
    return own?.status === 'complete' ? own : undefined;
  }
  return [...records.values()].find((r) => r.itemId === itemId && r.status === 'complete');
}

export function initDownloads(): Promise<void> {
  if (initialised) return initialised;
  initialised = (async () => {
    let all: DownloadRecord[] = [];
    try {
      all = await dbGetAll<DownloadRecord>('downloads');
    } catch {
      return;
    }
    for (const stored of all) {
      const r = withRowMetadata(stored);
      // A download that was running when the app closed, or that hit a
      // network error, resumes automatically.
      if (r.status === 'downloading' || (r.status === 'error' && r.retryable)) r.status = 'queued';
      records.set(keyOf(r), r);
      summaries.set(keyOf(r), summarize(r));
    }
    refreshStore();
    window.addEventListener('online', retryTransientErrors);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') retryTransientErrors();
    });
    // Queued downloads belong to one account; switching back to it resumes them.
    sessionStore.subscribe(() => {
      refreshStore();
      pump();
    });
    pump();
  })();
  return initialised;
}

let retryTimer: ReturnType<typeof setTimeout> | undefined;

/** Re-queues downloads that stopped on a network blip or server hiccup. */
function retryTransientErrors(): void {
  clearTimeout(retryTimer);
  if (navigator.onLine === false) return;
  for (const r of records.values()) {
    if (r.status === 'error' && r.retryable) void persist({ ...r, status: 'queued', error: undefined });
  }
  pump();
}

/** Asks the browser not to evict our storage under pressure. */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (await navigator.storage?.persisted?.()) return true;
    return (await navigator.storage?.persist?.()) ?? false;
  } catch {
    return false;
  }
}

/** Downloads an item for the signed-in account (resumes its paused or failed download). */
export async function queueDownload(itemId: string): Promise<void> {
  if (!isDownloadSupported()) throw new Error('This browser cannot store audiobooks offline.');
  const session = getSession();
  if (!session) throw new Error('Sign in first.');
  await initDownloads();
  const existing = records.get(downloadKey(session, itemId));
  if (existing && existing.status !== 'error' && existing.status !== 'paused') return;
  if (existing) {
    await persist({ ...existing, status: 'queued', error: undefined });
    pump();
    return;
  }

  void requestPersistentStorage();
  const item = await getItem(itemId);
  const files = (item.media.audioFiles ?? []).filter((f) => !f.exclude && !f.invalid).sort((a, b) => a.index - b.index);
  if (files.length === 0) throw new Error('This book has no audio files to download.');

  let offset = 0;
  const tracks: DownloadedTrack[] = files.map((f) => {
    const t: DownloadedTrack = {
      index: f.index,
      ino: f.ino,
      fileName: `${f.ino}${f.metadata.ext ?? ''}`.replace(/[^\w.-]/g, '_'),
      mimeType: f.mimeType ?? 'audio/mpeg',
      duration: f.duration,
      startOffset: offset,
      size: f.metadata.size ?? 0,
    };
    offset += f.duration;
    return t;
  });
  const totalBytes = tracks.reduce((n, t) => n + t.size, 0);

  // Check quota up-front so we fail fast instead of half-way through.
  try {
    const est = await navigator.storage.estimate();
    if (est.quota && est.usage !== undefined && totalBytes > est.quota - est.usage) {
      throw new Error('Not enough storage space on this device for this book.');
    }
  } catch (err) {
    if ((err as Error).message.startsWith('Not enough')) throw err;
  }

  let coverBlob: Blob | undefined;
  let serverHasCover = serverCoverFromItem(item);
  if (serverHasCover !== false) {
    try {
      const res = await fetch(coverUrl(item, 600), { credentials: 'omit' });
      if (res.ok) {
        coverBlob = await res.blob();
        serverHasCover = true;
      } else if (res.status === 404) serverHasCover ??= false;
    } catch {
      /* cover is optional */
    }
  }

  const md = item.media.metadata;
  const record: DownloadRecord = {
    itemId,
    serverUrl: session.serverUrl,
    userId: session.userId,
    folder: downloadFolderFor(session, itemId),
    title: md.title,
    author: md.authorName ?? md.authors?.map((a) => a.name).join(', ') ?? '',
    narrator: md.narratorName ?? md.narrators?.join(', '),
    duration: item.media.duration ?? offset,
    chapters: item.media.chapters ?? [],
    tracks,
    coverBlob,
    status: 'queued',
    totalBytes,
    downloadedBytes: 0,
    createdAt: Date.now(),
    item: stripItem(item),
    ...rowMetadata(item),
    coverBytes: coverBlob?.size,
    ...(serverHasCover !== undefined ? { serverHasCover } : {}),
  };
  await persist(record);
  pump();
}

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL('./download.worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (ev: MessageEvent<FromWorker>) => void onWorkerMessage(ev.data);
    worker.onerror = (ev) => {
      console.error('[downloads] worker crashed', ev);
      if (activeJob) {
        const r = records.get(activeJob.key);
        activeJob = null;
        if (r) void persist({ ...r, status: 'error', error: 'Download failed unexpectedly.' });
      }
      worker = null;
    };
  }
  return worker;
}

function pump() {
  if (activeJob) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
  const session = getSession();
  if (!session) return;
  const next = [...records.values()].filter((r) => r.status === 'queued' && belongsTo(r, session)).sort((a, b) => a.createdAt - b.createdAt)[0];
  if (!next) return;

  const files: DownloadFileSpec[] = next.tracks.map((t) => ({
    name: t.fileName,
    url: resolveServerPath(session.serverUrl, audioFilePath(next.itemId, t.ino)),
    size: t.size,
  }));
  const fileBase: number[] = [];
  let acc = 0;
  for (const t of next.tracks) {
    fileBase.push(acc);
    acc += t.size;
  }
  const jobId = `${next.itemId}:${Date.now()}`;
  activeJob = { jobId, key: keyOf(next), fileBase, fileBytes: [] };
  void persist({ ...next, status: 'downloading', error: undefined });
  const msg: ToWorker = { type: 'start', jobId, itemId: next.itemId, folder: folderOf(next), files, authHeader: `Bearer ${session.accessToken}` };
  getWorker().postMessage(msg);
}

let lastPersist = 0;
let authRetries = 0;

async function onWorkerMessage(msg: FromWorker) {
  if (!activeJob || msg.jobId !== activeJob.jobId) return;
  const r = records.get(activeJob.key);
  if (!r) return;
  switch (msg.type) {
    case 'progress': {
      const updated = { ...r, downloadedBytes: msg.totalBytes };
      publish(updated);
      if (Date.now() - lastPersist > 3000) {
        lastPersist = Date.now();
        void persist(updated);
      }
      break;
    }
    case 'file-done': {
      const base = activeJob.fileBase[msg.fileIndex] ?? 0;
      activeJob.fileBytes[msg.fileIndex] = msg.bytes;
      publish({ ...r, downloadedBytes: base + msg.bytes });
      authRetries = 0;
      break;
    }
    case 'done': {
      // The bytes really stored (the worker verified each file against its expected size).
      const stored = activeJob.fileBytes.reduce((sum, bytes) => sum + (bytes ?? 0), 0);
      activeJob = null;
      const done: DownloadRecord = { ...r, status: 'complete', downloadedBytes: stored || r.downloadedBytes, completedAt: Date.now() };
      await persist(done);
      if (settingsStore.get().notifyDownloads) {
        void notify('Download complete', { body: `${r.title} is ready to listen offline.`, tag: `dl-${r.itemId}`, url: `/item/${r.itemId}` });
      }
      pump();
      break;
    }
    case 'error': {
      activeJob = null;
      if (msg.cancelled) {
        // pause() / remove() already updated the record.
        pump();
        return;
      }
      if (msg.status === 401 && authRetries < 1 && belongsTo(r, getSession()) && (await refreshAccessToken())) {
        authRetries++;
        await persist({ ...r, status: 'queued' });
        pump();
        return;
      }
      const retryable = !msg.quota && (msg.status === undefined || msg.status === 429 || msg.status >= 500);
      await persist({ ...r, status: 'error', error: msg.message, retryable });
      // Connections drop without an 'offline' event (switching networks, tunnels): try again shortly.
      if (retryable) {
        clearTimeout(retryTimer);
        retryTimer = setTimeout(retryTransientErrors, 30_000);
      }
      if (msg.quota && settingsStore.get().notifyDownloads) {
        void notify('Download stopped', { body: `${r.title}: ${msg.message}`, tag: `dl-${r.itemId}`, url: '/downloads' });
      }
      pump();
      break;
    }
  }
}

/** Pauses a download (an item id means the visible download; a `DownloadSummary.key` names any account's). */
export async function pauseDownload(itemIdOrKey: string): Promise<void> {
  const r = recordFor(itemIdOrKey);
  if (!r || r.status === 'complete') return;
  if (activeJob?.key === keyOf(r)) {
    getWorker().postMessage({ type: 'cancel', jobId: activeJob.jobId } satisfies ToWorker);
  }
  await persist({ ...r, status: 'paused' });
}

/** Resumes a download (item id or `DownloadSummary.key`). Another account's download runs once that account is active. */
export async function resumeDownload(itemIdOrKey: string): Promise<void> {
  const r = recordFor(itemIdOrKey);
  if (!r || r.status === 'complete' || r.status === 'downloading') return;
  await persist({ ...r, status: 'queued', error: undefined });
  pump();
}

/** Deletes a download and its files (item id or `DownloadSummary.key`). */
export async function removeDownload(itemIdOrKey: string): Promise<void> {
  const r = recordFor(itemIdOrKey);
  if (!r) return;
  const key = keyOf(r);
  if (activeJob?.key === key) {
    getWorker().postMessage({ type: 'cancel', jobId: activeJob.jobId } satisfies ToWorker);
    activeJob = null;
  }
  records.delete(key);
  summaries.delete(key);
  refreshStore();
  try {
    await dbDelete('downloads', [r.serverUrl, r.userId, r.itemId]);
  } catch {
    /* ignore */
  }
  // The worker may still hold the file open for a moment after cancelling.
  for (let attempt = 0; attempt < 5; attempt++) {
    try {
      const root = await navigator.storage.getDirectory();
      const base = await root.getDirectoryHandle(DOWNLOADS_DIR, { create: true });
      await base.removeEntry(folderOf(r), { recursive: true });
      break;
    } catch (err) {
      if ((err as DOMException)?.name === 'NotFoundError') break;
      await new Promise((resolve) => setTimeout(resolve, 300));
    }
  }
  pump();
}

/** The full record (with cover and item) behind an item id's visible download or a `DownloadSummary.key`. */
export function getDownloadRecord(itemIdOrKey: string): DownloadRecord | undefined {
  return recordFor(itemIdOrKey);
}

/** Every account's downloads (storage metrics, "remove all"). */
export function listDownloads(): DownloadSummary[] {
  return [...summaries.values()];
}

/** OPFS folder name of a download's files. */
export function downloadFolderOf(summary: Pick<DownloadSummary, 'folder' | 'itemId'>): string {
  return folderOf(summary);
}

export interface LocalPlayback {
  record: DownloadRecord;
  urls: string[];
  coverUrl?: string;
  revoke(): void;
}

export const DOWNLOAD_INCOMPLETE_MESSAGE = 'Downloaded files are incomplete. Tap to download again.';
export const DOWNLOAD_UNPLAYABLE_MESSAGE = "This download couldn't be played. Tap to download again.";

/**
 * Opens the downloaded files as disk-backed File objects and returns blob URLs
 * for `<audio>`. Each file is checked first (ENG-002, Flutter
 * `_verifyLocalPlaybackFile`): a missing file, one under 1 KB, or one short of
 * its expected size beyond 1 % / 64 KB marks the download failed ("Tap to
 * download again"; the bytes stay, so that resumes) and returns null, so the
 * player streams the book instead.
 */
export async function openLocalPlayback(itemId: string): Promise<LocalPlayback | null> {
  await initDownloads();
  const record = playableRecord(itemId);
  if (!record) return null;
  try {
    const root = await navigator.storage.getDirectory();
    const dir = await (await root.getDirectoryHandle(DOWNLOADS_DIR)).getDirectoryHandle(folderOf(record));
    const files: File[] = [];
    for (const t of record.tracks) files.push(await (await dir.getFileHandle(t.fileName)).getFile());
    if (record.tracks.some((t, i) => isUnplayableLocalFile(files[i]!.size, t.size))) {
      const stored = files.reduce((sum, f) => sum + f.size, 0);
      console.warn('[downloads] stored files are incomplete, streaming instead', { itemId, stored, expected: record.totalBytes });
      await persist({ ...record, status: 'error', error: DOWNLOAD_INCOMPLETE_MESSAGE, retryable: false, downloadedBytes: stored });
      return null;
    }
    // Re-wrap with the right MIME type; OPFS files have none, which Safari refuses to play.
    const urls = files.map((file, i) => URL.createObjectURL(file.type ? file : new Blob([file], { type: record.tracks[i]!.mimeType })));
    const cover = record.coverBlob ? URL.createObjectURL(record.coverBlob) : undefined;
    return {
      record,
      urls,
      coverUrl: cover,
      revoke: () => {
        urls.forEach((u) => URL.revokeObjectURL(u));
        if (cover) URL.revokeObjectURL(cover);
      },
    };
  } catch (err) {
    console.error('[downloads] files missing, marking for re-download', err);
    await persist({ ...record, status: 'error', error: 'Downloaded files were removed by the system. Tap to download again.', retryable: false, downloadedBytes: 0 });
    return null;
  }
}

/**
 * Takes a downloaded book that failed while playing out of use (ENG-003,
 * Flutter quarantine): the signed-in account's record is marked failed ("Tap
 * to download again") so playback streams instead. `discardFiles` also
 * deletes the stored audio (a corrupt file would only fail again); without
 * it the bytes stay and the next download resumes them. Returns false when
 * there was no playable download to take out of use.
 */
export async function quarantineDownload(itemId: string, opts: { discardFiles?: boolean; message?: string } = {}): Promise<boolean> {
  await initDownloads();
  const record = playableRecord(itemId);
  if (!record || !getSession()) return false;
  await persist({ ...record, status: 'error', error: opts.message ?? DOWNLOAD_UNPLAYABLE_MESSAGE, retryable: false, ...(opts.discardFiles ? { downloadedBytes: 0 } : {}) });
  if (opts.discardFiles) {
    try {
      const root = await navigator.storage.getDirectory();
      const base = await root.getDirectoryHandle(DOWNLOADS_DIR, { create: true });
      await base.removeEntry(folderOf(record), { recursive: true });
    } catch (err) {
      if ((err as DOMException)?.name !== 'NotFoundError') console.warn('[downloads] could not delete unplayable files', err);
    }
  }
  return true;
}

export async function getCoverObjectUrl(itemIdOrKey: string): Promise<string | undefined> {
  await initDownloads();
  const blob = recordFor(itemIdOrKey)?.coverBlob;
  return blob ? URL.createObjectURL(blob) : undefined;
}

export interface StorageInfo {
  usage: number;
  quota: number;
  persisted: boolean;
}

export async function getStorageInfo(): Promise<StorageInfo> {
  try {
    const [est, persisted] = await Promise.all([navigator.storage.estimate(), navigator.storage.persisted?.() ?? false]);
    return { usage: est.usage ?? 0, quota: est.quota ?? 0, persisted };
  } catch {
    return { usage: 0, quota: 0, persisted: false };
  }
}

/** Test hook. */
export function __resetDownloadsForTests() {
  records.clear();
  summaries.clear();
  activeJob = null;
  initialised = null;
  downloadsStore.set({});
}

/** Test hook: adds records as if loaded from IndexedDB. */
export function __seedDownloadsForTests(list: DownloadRecord[]): void {
  for (const r of list) {
    records.set(keyOf(r), r);
    summaries.set(keyOf(r), summarize(r));
  }
  refreshStore();
}
